import { z } from 'zod';
import { activePersonSchema } from './activity.js';

export const projectSummarySchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  name: z.string(),
  description: z.string(),
  revision: z.number().int(),
  schemaVersion: z.number().int(),
  parentProjectId: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /** Small 3D preview for the project list; the URL changes whenever the image does. */
  thumbnailUrl: z.string().nullable(),
  /** What the requesting user may do: own (or admin), edit a shared project, or only view it. */
  access: z.enum(['owner', 'editor', 'viewer']).optional(),
  /** Owner's display name, for projects shared with the requesting user. */
  ownerName: z.string().optional(),
  /** Others have access too: the editor then works live with them. */
  shared: z.boolean().optional(),
  /** Others working in the project right now (project list only; never the requesting user). */
  activeUsers: z.array(activePersonSchema).optional(),
});
export type ProjectSummary = z.infer<typeof projectSummarySchema>;
export type ProjectAccess = NonNullable<ProjectSummary['access']>;

export const memberRoleSchema = z.enum(['editor', 'viewer']);
export type MemberRole = z.infer<typeof memberRoleSchema>;

export const projectMemberSchema = z.object({
  userId: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: memberRoleSchema,
});
export type ProjectMember = z.infer<typeof projectMemberSchema>;

export const projectMembersResponseSchema = z.object({
  owner: z.object({ userId: z.string(), email: z.string(), displayName: z.string() }),
  members: z.array(projectMemberSchema),
});
export type ProjectMembersResponse = z.infer<typeof projectMembersResponseSchema>;

export const addMemberRequestSchema = z.object({
  email: z.string().trim().toLowerCase().pipe(z.email().max(254)),
  role: memberRoleSchema,
});
export const updateMemberRequestSchema = z.object({ role: memberRoleSchema });

/** Preview images are small (about 480 × 360 px JPEG); the limit leaves room for PNG. */
export const MAX_THUMBNAIL_CHARS = 700_000;
export const MAX_THUMBNAIL_SIDE = 1024;

export const projectThumbnailRequestSchema = z.object({
  dataUrl: z
    .string()
    .max(MAX_THUMBNAIL_CHARS)
    .regex(/^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/),
});
export type ProjectThumbnailRequest = z.infer<typeof projectThumbnailRequestSchema>;

export const projectListResponseSchema = z.object({ projects: z.array(projectSummarySchema) });
export type ProjectListResponse = z.infer<typeof projectListResponseSchema>;

/**
 * The document is typed as unknown on the wire on purpose: both sides run it through
 * project-model's migrate + validate pipeline instead of trusting the transport schema.
 */
export const projectResponseSchema = z.object({
  project: projectSummarySchema,
  document: z.unknown(),
});
export type ProjectResponse = z.infer<typeof projectResponseSchema>;

export const createProjectRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: z.string().max(10000).optional(),
});
export type CreateProjectRequest = z.infer<typeof createProjectRequestSchema>;

export const updateProjectRequestSchema = z.object({
  baseRevision: z.number().int().nonnegative(),
  document: z.unknown(),
});
export type UpdateProjectRequest = z.infer<typeof updateProjectRequestSchema>;

export const duplicateProjectRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
});
export type DuplicateProjectRequest = z.infer<typeof duplicateProjectRequestSchema>;

export const conflictDetailsSchema = z.object({ currentRevision: z.number().int() });
export type ConflictDetails = z.infer<typeof conflictDetailsSchema>;

export const updateMetaRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(10000).optional(),
  })
  .refine((v) => v.name !== undefined || v.description !== undefined, {
    message: 'Keine Änderung angegeben',
  });
export type UpdateMetaRequest = z.infer<typeof updateMetaRequestSchema>;

/** Save a diverging local state as a new project instead of overwriting (conflict handling). */
export const forkProjectRequestSchema = z.object({
  name: z.string().trim().min(1).max(200),
  document: z.unknown(),
});
export type ForkProjectRequest = z.infer<typeof forkProjectRequestSchema>;

export const versionSummarySchema = z.object({
  id: z.string(),
  revision: z.number().int(),
  label: z.string().nullable(),
  auto: z.boolean(),
  createdAt: z.string(),
  createdBy: z.string(),
});
export type VersionSummary = z.infer<typeof versionSummarySchema>;

export const versionListResponseSchema = z.object({ versions: z.array(versionSummarySchema) });
export const versionResponseSchema = z.object({
  version: versionSummarySchema,
  document: z.unknown(),
});

export const createVersionRequestSchema = z.object({ label: z.string().trim().min(1).max(120) });
/** Automatic backups older than this many days are removed; named versions stay. */
export const pruneVersionsRequestSchema = z.object({
  olderThanDays: z.number().int().min(1).max(3650),
});
export const pruneVersionsResponseSchema = z.object({ deleted: z.number().int() });
export const restoreVersionRequestSchema = z.object({
  baseRevision: z.number().int().nonnegative(),
});
